import { useState } from "react";
import axios from "axios";
import { CheckCircle2, Send, AlertCircle } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "NDIS Support Coordination",
  "Psychosocial Recovery Coaching (PSRC)",
  "Early Childhood Approach Supports (Ages 0 to 9)",
  "Employment Assistance (SLES / 1:1)",
  "DVA Social Work & Wellbeing Coordination",
  "Domestic & Family Violence Social Work",
  "Sport & Performance Social Work",
  "Other",
];

export default function Referrals() {
  usePageMeta({
    title: "Make a Referral, Navica Supports NDIS",
    description: "Refer an NDIS participant to Navica Supports for Support Coordination, Recovery Coaching, or Case Management. Sydney-based, one business day response.",
  });
  const [form, setForm] = useState({
    referral_type: "ndis",
    referral_source: "",
    first_name: "",
    last_name: "",
    ndis_number: "",
    date_of_birth: "",
    phone: "",
    email: "",
    plan_start_date: "",
    plan_end_date: "",
    gender: "",
    identifies_atsi: "",
    address: "",
    primary_disability: "",
    reason_for_referral: "",
    representative_details: "",
    services_requested: [],
    services_other: "",
    sexual_identity: "",
    consent: false,
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const toggleService = (service) => {
    setForm((f) => ({
      ...f,
      services_requested: f.services_requested.includes(service)
        ? f.services_requested.filter((s) => s !== service)
        : [...f.services_requested, service],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      setStatus({ state: "error", message: "Please confirm consent to proceed." });
      return;
    }
    setStatus({ state: "submitting", message: "" });
    try {
      const { consent, services_other, services_requested, ...rest } = form;
      let finalServices;
      if (rest.referral_type === "other") {
        // For "Other" referral type, the freeform text is the whole request
        finalServices = services_other.trim() ? [`Other: ${services_other.trim()}`] : [];
      } else {
        // Merge free-text "Other" description into services_requested list
        finalServices = services_requested.map((s) =>
          s === "Other" && services_other.trim()
            ? `Other: ${services_other.trim()}`
            : s
        );
      }
      const payload = { ...rest, services_requested: finalServices };
      await axios.post(`${API}/referrals`, payload);
      setStatus({ state: "success", message: "Referral submitted. We'll be in touch within one business day." });
      setForm({
        referral_type: "ndis", referral_source: "",
        first_name: "", last_name: "", ndis_number: "", date_of_birth: "", phone: "", email: "",
        plan_start_date: "", plan_end_date: "", gender: "", identifies_atsi: "", address: "",
        primary_disability: "", reason_for_referral: "", representative_details: "",
        services_requested: [], services_other: "", sexual_identity: "", consent: false,
      });
    } catch (err) {
      setStatus({ state: "error", message: err?.response?.data?.detail || "Something went wrong. Please try again or call us directly." });
    }
  };

  if (status.state === "success") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-5" data-testid="referral-success">
        <div className="max-w-lg w-full bg-white rounded-[2.5rem] p-10 lg:p-14 border border-[#F0E5DE] text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#FCE7EB] flex items-center justify-center mb-6">
            <CheckCircle2 className="w-8 h-8 text-[#E2718A]" />
          </div>
          <h1 className="font-display text-3xl lg:text-4xl text-[#1F2A2E] mb-4">Thank you.</h1>
          <p className="text-[#4A595E] leading-relaxed mb-7">{status.message}</p>
          <a href="/" className="btn-primary">Back to home</a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="referrals-page">
      {/* HERO */}
      <section className="pt-16 lg:pt-20 pb-10">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <div className="text-sm uppercase tracking-[0.2em] text-[#EA823A] font-semibold mb-4">Participant Referral</div>
          <h1 className="font-display text-5xl md:text-6xl text-[#1F2A2E] leading-tight mb-5">
            Make a <span className="italic" style={{ color: "var(--navica-coral)" }}>referral</span>.
          </h1>
          <p className="text-lg text-[#4A595E] leading-relaxed">
            Thank you for making contact with the team at Navica Supports. Please provide the following information to help us understand your needs and goals. Your information will remain confidential and will be used solely for service provision.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto px-5 lg:px-8" data-testid="referral-form">
          {/* Referral type selector */}
          <FormSection title="Referral type" badge="00">
            <p className="text-sm text-[#6B7A80] mb-4">Which service is this referral for? We&apos;ll show only the fields relevant to that stream.</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {[
                { value: "ndis", label: "NDIS Participant" },
                { value: "dva", label: "DVA" },
                { value: "dfv", label: "Domestic/Family Violence" },
                { value: "athlete", label: "Athlete Wellbeing" },
                { value: "other", label: "Other" },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-center justify-center gap-2 px-3 py-3 rounded-2xl border text-sm font-medium text-center cursor-pointer transition ${
                    form.referral_type === opt.value ? "bg-[#FCE7EB] border-[#E2718A] text-[#1F2A2E]" : "bg-white border-[#F0E5DE] text-[#284952] hover:border-[#F4C3CC]"
                  }`}
                >
                  <input
                    type="radio" name="referral_type" value={opt.value}
                    checked={form.referral_type === opt.value}
                    onChange={(e) => update("referral_type", e.target.value)}
                    className="hidden"
                    data-testid={`ref-type-${opt.value}`}
                  />
                  {opt.label}
                </label>
              ))}
            </div>

            <div className="mt-6">
              <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-2">
                Where is the person right now?
              </label>
              <select
                className="input-navica"
                value={form.referral_source}
                onChange={(e) => update("referral_source", e.target.value)}
                data-testid="ref-source"
              >
                <option value="">- Select (optional but helpful) -</option>
                <option value="Community">In the community / at home</option>
                <option value="Hospital">In hospital</option>
                <option value="Custody">In custody / correctional facility</option>
                <option value="Rehab">In a rehab or residential facility</option>
                <option value="Homelessness">Sleeping rough / homelessness</option>
                <option value="Other">Somewhere else</option>
              </select>
              <p className="text-xs text-[#6B7A80] mt-1.5">This helps us triage, hospital and custody referrals are prioritised.</p>
            </div>
          </FormSection>

          {/* Participant Details */}
          <FormSection title={form.referral_type === "ndis" ? "Participant Details" : "Person's Details"} badge="01">
            <Row>
              <Field label="First Name" required>
                <input className="input-navica" value={form.first_name} onChange={(e) => update("first_name", e.target.value)} required data-testid="ref-first-name" />
              </Field>
              <Field label="Last Name" required>
                <input className="input-navica" value={form.last_name} onChange={(e) => update("last_name", e.target.value)} required data-testid="ref-last-name" />
              </Field>
            </Row>
            <Row>
              {form.referral_type === "ndis" ? (
                <Field label="NDIS Number" required>
                  <input className="input-navica" value={form.ndis_number} onChange={(e) => update("ndis_number", e.target.value)} required data-testid="ref-ndis-number" />
                </Field>
              ) : (
                <Field label="Date of Birth">
                  <input type="date" className="input-navica" value={form.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} data-testid="ref-dob" />
                </Field>
              )}
              {form.referral_type === "ndis" && (
                <Field label="Date of Birth" required>
                  <input type="date" className="input-navica" value={form.date_of_birth} onChange={(e) => update("date_of_birth", e.target.value)} required data-testid="ref-dob" />
                </Field>
              )}
            </Row>
            <Row>
              <Field label="Phone Number">
                <input type="tel" className="input-navica" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="ref-phone" />
              </Field>
              <Field label="Email Address">
                <input type="email" className="input-navica" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="ref-email" />
              </Field>
            </Row>
            {form.referral_type === "ndis" && (
              <Row>
                <Field label="Plan Start Date">
                  <input type="date" className="input-navica" value={form.plan_start_date} onChange={(e) => update("plan_start_date", e.target.value)} data-testid="ref-plan-start" />
                </Field>
                <Field label="Plan End Date">
                  <input type="date" className="input-navica" value={form.plan_end_date} onChange={(e) => update("plan_end_date", e.target.value)} data-testid="ref-plan-end" />
                </Field>
              </Row>
            )}
            <Row>
              <Field label="Gender" required={form.referral_type === "ndis"}>
                <div className="flex flex-wrap gap-4 pt-2">
                  {["Male", "Female", "Non-binary", "Prefer not to say"].map((g) => (
                    <label key={g} className="flex items-center gap-2 text-[#284952] cursor-pointer">
                      <input type="radio" name="gender" value={g} checked={form.gender === g} onChange={(e) => update("gender", e.target.value)} className="navica-check" required={form.referral_type === "ndis"} data-testid={`ref-gender-${g.toLowerCase().replace(/\s+/g, "-")}`} />
                      <span>{g}</span>
                    </label>
                  ))}
                </div>
              </Field>
              <Field label="Identifies as Aboriginal or Torres Strait Islander?">
                <div className="flex gap-6 pt-2">
                  {["Yes", "No", "Prefer not to say"].map((v) => (
                    <label key={v} className="flex items-center gap-2 text-[#284952] cursor-pointer">
                      <input type="radio" name="atsi" value={v} checked={form.identifies_atsi === v} onChange={(e) => update("identifies_atsi", e.target.value)} className="navica-check" data-testid={`ref-atsi-${v.toLowerCase().replace(/\s+/g, "-")}`} />
                      <span>{v}</span>
                    </label>
                  ))}
                </div>
              </Field>
            </Row>
            <Row>
              <Field label="Sexual identity (optional)">
                <input
                  className="input-navica"
                  value={form.sexual_identity}
                  onChange={(e) => update("sexual_identity", e.target.value)}
                  placeholder="Heterosexual, gay, lesbian, bisexual, queer, gender fluid, etc., or prefer not to say"
                  data-testid="ref-sexual-identity"
                />
              </Field>
            </Row>
            <Field
              label={
                form.referral_type === "ndis" ? "Participant Address" :
                form.referral_type === "dfv" ? "Address (optional, leave blank for safety if needed)" :
                "Address"
              }
              required={form.referral_type !== "dfv"}
            >
              <input
                className="input-navica"
                value={form.address}
                onChange={(e) => update("address", e.target.value)}
                placeholder={form.referral_type === "dfv" ? "Only share if safe to do so" : "Street, suburb, postcode, state"}
                required={form.referral_type !== "dfv"}
                data-testid="ref-address"
              />
            </Field>
            <Row>
              {form.referral_type === "ndis" ? (
                <Field label="Primary Disability" required>
                  <input className="input-navica" value={form.primary_disability} onChange={(e) => update("primary_disability", e.target.value)} required data-testid="ref-disability" />
                </Field>
              ) : (
                <Field label={
                  form.referral_type === "dva" ? "Service background / DVA context" :
                  form.referral_type === "dfv" ? "Current situation (safe to share)" :
                  form.referral_type === "athlete" ? "Sport / club" :
                  "Background"
                }>
                  <input className="input-navica" value={form.primary_disability} onChange={(e) => update("primary_disability", e.target.value)} data-testid="ref-context" />
                </Field>
              )}
              <Field label="Reason for Referral" required={form.referral_type !== "ndis"}>
                <input className="input-navica" value={form.reason_for_referral} onChange={(e) => update("reason_for_referral", e.target.value)} required={form.referral_type !== "ndis"} data-testid="ref-reason" />
              </Field>
            </Row>
            <Field label={form.referral_type === "ndis" ? "Representative, Plan Nominee or Guardian Details" : "Support person / Nominated contact"}>
              <textarea rows={3} className="input-navica" value={form.representative_details} onChange={(e) => update("representative_details", e.target.value)} placeholder="Name, relationship, contact details (if applicable)" data-testid="ref-representative" />
            </Field>
          </FormSection>

          {/* Services Requested */}
          <FormSection title="Services Requested" badge="02">
            {form.referral_type === "other" ? (
              <>
                <p className="text-sm text-[#6B7A80] mb-3">Tell us what kind of support you&apos;re looking for, a rough description in your own words is plenty.</p>
                <textarea
                  rows={4}
                  className="input-navica"
                  placeholder="E.g. one-off letter of support, brief counselling, help finding a specific service..."
                  value={form.services_other}
                  onChange={(e) => update("services_other", e.target.value)}
                  data-testid="ref-service-other-freeform"
                />
              </>
            ) : (
              <>
                <p className="text-sm text-[#6B7A80] mb-5">Tick all the services this person is interested in. Pick <em>Other</em> if what you need isn&apos;t listed.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {SERVICE_OPTIONS.filter((s) => {
                    if (s === "Other") return true;
                    if (form.referral_type === "ndis") {
                      // Only NDIS-relevant services (exclude DVA, DFV, athlete)
                      return !s.includes("DVA") && !s.includes("Domestic") && !s.includes("Sport");
                    }
                    if (form.referral_type === "dva") return s.includes("DVA");
                    if (form.referral_type === "dfv") return s.includes("Domestic");
                    if (form.referral_type === "athlete") return s.includes("Sport");
                    return s.includes("DVA") || s.includes("Domestic") || s.includes("Sport");
                  }).map((s) => {
                    const checked = form.services_requested.includes(s);
                    return (
                      <label
                        key={s}
                        className={`flex items-center gap-3 p-4 rounded-2xl border cursor-pointer transition ${
                          checked ? "bg-[#FCE7EB] border-[#E2718A]" : "bg-white border-[#F0E5DE] hover:border-[#F4C3CC]"
                        }`}
                      >
                        <input type="checkbox" checked={checked} onChange={() => toggleService(s)} className="navica-check" data-testid={`ref-service-${s.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`} />
                        <span className="text-[#284952] font-medium text-sm">{s}</span>
                      </label>
                    );
                  })}
                </div>

                {form.services_requested.includes("Other") && (
                  <div className="mt-5" data-testid="ref-service-other-wrap">
                    <label className="block text-xs uppercase tracking-wider font-semibold text-[#6B7A80] mb-1.5">
                      Please describe what you&apos;re looking for
                    </label>
                    <textarea
                      rows={3}
                      className="input-navica"
                      placeholder="Tell us what kind of support you need, even a rough description helps."
                      value={form.services_other}
                      onChange={(e) => update("services_other", e.target.value)}
                      data-testid="ref-service-other-input"
                    />
                  </div>
                )}
              </>
            )}
          </FormSection>

          {/* Consent */}
          <FormSection title="Consent" badge="03">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.consent} onChange={(e) => update("consent", e.target.checked)} className="navica-check mt-1" data-testid="ref-consent" />
              <span className="text-sm text-[#4A595E] leading-relaxed">
                I consent to Navica Supports collecting and using this information for the purpose of providing the requested services, in accordance with their Privacy Policy. I understand my information will be kept confidential.
              </span>
            </label>
          </FormSection>

          {status.state === "error" && (
            <div className="mb-6 p-4 rounded-2xl bg-red-50 border border-red-200 flex gap-3 items-start" data-testid="ref-error">
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{status.message}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={status.state === "submitting"}
            className="btn-primary w-full sm:w-auto"
            data-testid="ref-submit"
          >
            {status.state === "submitting" ? "Submitting..." : "Submit Referral"} <Send className="w-4 h-4" />
          </button>
        </form>
      </section>
    </div>
  );
}

function FormSection({ title, badge, children }) {
  return (
    <div className="bg-white rounded-[2rem] border border-[#F0E5DE] p-7 lg:p-10 mb-6">
      <div className="flex items-center gap-3 mb-7 pb-5 border-b border-[#F0E5DE]">
        <span className="font-display text-3xl text-[#F4C3CC]">{badge}</span>
        <h2 className="font-display text-2xl text-[#1F2A2E]">{title}</h2>
      </div>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid md:grid-cols-2 gap-5">{children}</div>;
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#284952] mb-2">
        {label} {required && <span className="text-[#E2718A]">*</span>}
      </label>
      {children}
    </div>
  );
}
